import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

type ToastTone = 'success' | 'danger' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  duration: number;
}

const ICONS: Record<ToastTone, JSX.Element> = {
  danger: <AlertCircle className="w-4 h-4" />,
  info: <Info className="w-4 h-4" />,
  success: <CheckCircle2 className="w-4 h-4" />,
};

/** Icon chip — the only place tone color appears; the card itself stays neutral. */
const ICON_WRAP_CLASSES: Record<ToastTone, string> = {
  danger: 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400',
  info: 'bg-zinc-100 text-zinc-500 dark:bg-white/10 dark:text-zinc-400',
  success: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
};

/** Left accent stripe — a quieter tone signal than a fully tinted card. */
const ACCENT_CLASSES: Record<ToastTone, string> = {
  danger: 'border-l-rose-500',
  info: 'border-l-zinc-300 dark:border-l-zinc-600',
  success: 'border-l-emerald-500',
};

// ── Module-level store ──────────────────────────────────────────────
// A plain external store (not React context) so `toast.*` can be called
// from outside the component tree — e.g. the global mutation error
// handler in `queryClient.ts`.
let items: ToastItem[] = [];
let nextId = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ToastItem[] {
  return items;
}

function dismiss(id: number): void {
  items = items.filter((item) => item.id !== id);
  emit();
}

function show(tone: ToastTone, message: string, options?: { duration?: number }): void {
  const duration = options?.duration ?? (tone === 'danger' ? 6000 : 4000);
  items = [...items, { id: nextId++, tone, message, duration }];
  emit();
}

export const toast = {
  success: (message: string, options?: { duration?: number }) => show('success', message, options),
  error: (message: string, options?: { duration?: number }) => show('danger', message, options),
  info: (message: string, options?: { duration?: number }) => show('info', message, options),
};

function ToastCard({ id, tone, message, duration }: ToastItem): JSX.Element {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const start = (): void => {
    timerRef.current = setTimeout(() => dismiss(id), duration);
  };
  const stop = (): void => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  useEffect(() => {
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      onMouseEnter={stop}
      onMouseLeave={start}
      className={`pointer-events-auto flex items-center gap-3 w-full py-3 pl-3.5 pr-3 rounded-xl border-y border-r border-zinc-200 dark:border-white/10 border-l-[3px] ${ACCENT_CLASSES[tone]} bg-white dark:bg-zinc-900 shadow-lg shadow-zinc-900/10 dark:shadow-glass-dark`}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <div
        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${ICON_WRAP_CLASSES[tone]}`}
      >
        {ICONS[tone]}
      </div>
      <div className="flex-1 text-sm font-medium text-zinc-800 dark:text-zinc-100 leading-snug">
        {message}
      </div>
      <button
        type="button"
        onClick={() => dismiss(id)}
        aria-label="Dismiss notification"
        className="shrink-0 -m-1 p-1 rounded-full text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-white/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

/** Bottom-right toast stack. Mount once near the root of the app. */
export function Toaster(): JSX.Element {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return createPortal(
    <div className="fixed bottom-6 right-6 z-[2000] flex flex-col-reverse gap-3 w-full max-w-sm pointer-events-none">
      <AnimatePresence initial={false}>
        {current.map((item) => (
          <ToastCard key={item.id} {...item} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
